import React from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { MENU, POLES, INSCRIPTIONS_URL } from "@/lib/data";
import { NAV } from "@/constants/testIds";

export default function Header() {
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const [polesOpen, setPolesOpen] = React.useState(false);
  const location = useLocation();

  React.useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);

  React.useEffect(() => { setOpen(false); setPolesOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid={NAV.header}
      className={`fixed top-0 inset-x-0 z-50 transition-colors ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-black/5" : "bg-white/70 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid={NAV.logo} className="flex items-center gap-2 shrink-0" aria-label="Heel'Events, accueil">
          <img src="/logos/heelevents.png" alt="Heel'Events" className="h-9 w-auto" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-6 font-body text-sm">
          {MENU.map((item) =>
            item.children ? (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => setPolesOpen(true)}
                onMouseLeave={() => setPolesOpen(false)}
              >
                <button
                  className="flex items-center gap-1 text-neutral-800 hover:text-[var(--he-red)] transition-colors"
                  data-testid="nav-poles-toggle"
                >
                  {item.label} <ChevronDown className="w-4 h-4" />
                </button>
                {polesOpen && (
                  <div className="absolute left-0 top-full pt-3">
                    <div className="min-w-[220px] bg-white border border-black/10 rounded-lg overflow-hidden shadow-sm">
                      {item.children.map((c) => (
                        <Link
                          key={c.to}
                          to={c.to}
                          className="block px-4 py-2.5 text-sm hover:bg-neutral-50 hover:text-[var(--he-red)] transition-colors"
                          data-testid={`nav-pole-${c.to.split("/").pop()}`}
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `transition-colors hover:text-[var(--he-red)] ${isActive ? "text-[var(--he-red)]" : "text-neutral-800"}`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={INSCRIPTIONS_URL}
            target="_blank"
            rel="noreferrer"
            data-testid={NAV.ctaInscription}
            className="btn-red rounded-full px-4 py-2 text-xs sm:text-sm"
          >
            Je m’inscris
          </a>
          <button
            className="lg:hidden p-2 -mr-2 text-neutral-800"
            aria-label="Ouvrir le menu"
            onClick={() => setOpen((v) => !v)}
            data-testid={NAV.menuToggle}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-white border-t border-black/5" data-testid={NAV.mobileMenu}>
          <nav className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1 font-body text-[0.95rem]">
            {MENU.map((item) =>
              item.children ? (
                <details key={item.label} className="group">
                  <summary className="flex items-center justify-between py-2.5 cursor-pointer list-none">
                    <span>{item.label}</span>
                    <ChevronDown className="w-4 h-4 group-open:rotate-180 transition-transform" />
                  </summary>
                  <div className="pl-4 pb-2 flex flex-col">
                    {item.children.map((c) => (
                      <Link
                        key={c.to}
                        to={c.to}
                        className="py-2 text-sm text-neutral-700 hover:text-[var(--he-red)]"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </details>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `py-2.5 border-b border-black/5 last:border-0 ${isActive ? "text-[var(--he-red)]" : "text-neutral-800"}`
                  }
                >
                  {item.label}
                </NavLink>
              )
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
